import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import {
  AlertController,
  ModalController,
  PopoverController,
} from '@ionic/angular/standalone';
import { filter } from 'rxjs';

type OverlayController = {
  getTop: () => Promise<{ dismiss: () => Promise<boolean> } | undefined>;
};

/**
 * PWA / モバイルブラウザで「戻る」や画面遷移したとき、
 * Popover / Modal / Alert が前面に残らないようにする。
 *
 * `experimentalCloseWatcher` が効く環境では戻るで先に閉じる。
 * 効かない環境・フッター切替などでは NavigationStart で閉じる。
 */
@Injectable({ providedIn: 'root' })
export class DismissOverlaysOnNavigateService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly popoverController = inject(PopoverController);
  private readonly modalController = inject(ModalController);
  private readonly alertController = inject(AlertController);
  private started = false;

  start(): void {
    if (this.started) return;
    this.started = true;

    this.router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        void this.dismissAllPresented(this.popoverController);
        void this.dismissAllPresented(this.modalController);
        void this.dismissAllPresented(this.alertController);
      });
  }

  /** 積み上がった overlay があれば上から順に全部閉じる。 */
  private async dismissAllPresented(
    controller: OverlayController,
  ): Promise<void> {
    for (;;) {
      let top: Awaited<ReturnType<OverlayController['getTop']>>;
      try {
        top = await controller.getTop();
      } catch {
        return;
      }
      if (!top) return;
      try {
        await top.dismiss();
      } catch {
        // 遷移中・Close Watcher 側で既に閉じている場合がある
        return;
      }
    }
  }
}
